import axios, {
    type AxiosError,
    type AxiosInstance,
    type AxiosProgressEvent,
    type AxiosRequestConfig,
    type AxiosRequestTransformer,
    type AxiosResponse,
    type AxiosResponseTransformer,
} from 'axios';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import qs from 'qs';
import { redirectToSignIn } from '@/lib/sign-in-redirect';

declare module 'axios' {
    export interface AxiosRequestConfig {
        skipAuthRedirect?: boolean;
        skipAuthRefresh?: boolean;
        retriedAfterRefresh?: boolean;
        requestStartedAt?: number;
    }
}

export interface HttpClientOptions {
    baseURL?: () => string;
    refreshSession?: () => Promise<void>;
    pendingSessionRefresh?: () => Promise<void> | null;
    transformParams?: (
        params: Record<string, unknown>,
    ) => Record<string, unknown>;
}

// Every client shares one login session, so refresh state is shared.
let lastRefreshCompletedAt = 0;

export class HttpClient {
    private readonly axios: AxiosInstance = createAxiosInstance();

    constructor(private readonly options: HttpClientOptions = {}) {
        this.axios.interceptors.request.use(async (config) => {
            if (!config.skipAuthRefresh) {
                await options.pendingSessionRefresh?.();
            }
            config.baseURL ??= options.baseURL?.();
            if (config.params && options.transformParams) {
                config.params = options.transformParams(config.params);
            }
            config.requestStartedAt = Date.now();
            return config;
        });
        this.axios.interceptors.response.use(
            (response) => response,
            (error: AxiosError) => this.handleResponseError(error),
        );
    }

    async get<T>(
        url: string,
        params?: Record<string, unknown>,
        config: AxiosRequestConfig = {},
    ): Promise<T> {
        const response = await this.axios.get<T>(url, { params, ...config });
        return response.data;
    }

    getResponse<T>(
        url: string,
        config: AxiosRequestConfig = {},
    ): Promise<AxiosResponse<T>> {
        return this.axios.get<T>(url, config);
    }

    async post<T>(
        url: string,
        data?: unknown,
        config: AxiosRequestConfig = {},
        onProgress?: (percent: number) => void,
    ): Promise<T> {
        const response = await this.axios.post<T>(url, data, {
            ...(onProgress && {
                onUploadProgress: toUploadProgressHandler(onProgress),
            }),
            ...config,
        });
        return response.data;
    }

    async put<T>(
        url: string,
        data?: unknown,
        config: AxiosRequestConfig = {},
    ): Promise<T> {
        const response = await this.axios.put<T>(url, data, config);
        return response.data;
    }

    async patch<T>(
        url: string,
        data?: unknown,
        config: AxiosRequestConfig = {},
    ): Promise<T> {
        const response = await this.axios.patch<T>(url, data, config);
        return response.data;
    }

    async delete<T>(url: string, config: AxiosRequestConfig = {}): Promise<T> {
        const response = await this.axios.delete<T>(url, config);
        return response.data;
    }

    private async handleResponseError(error: AxiosError): Promise<unknown> {
        const request = error.config;
        if (error.response?.status !== 401 || !request) {
            throw error;
        }

        if (await this.refreshAfterUnauthorized(request)) {
            return this.axios(request);
        }

        if (!request.skipAuthRedirect) {
            redirectToSignIn();
        }
        throw error;
    }

    private async refreshAfterUnauthorized(
        request: AxiosRequestConfig,
    ): Promise<boolean> {
        const { refreshSession } = this.options;
        if (
            !refreshSession ||
            request.skipAuthRefresh ||
            request.retriedAfterRefresh
        ) {
            return false;
        }

        const alreadyRefreshedSinceSent =
            (request.requestStartedAt ?? 0) < lastRefreshCompletedAt;
        request.retriedAfterRefresh = !alreadyRefreshedSinceSent;
        if (alreadyRefreshedSinceSent) {
            return true;
        }

        try {
            await refreshSession();
            lastRefreshCompletedAt = Date.now();
            return true;
        } catch {
            return false;
        }
    }
}

function createAxiosInstance(): AxiosInstance {
    return axios.create({
        withCredentials: true,
        paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true }),
        transformRequest: [
            (data) =>
                isPlainObjectOrArray(data)
                    ? snakecaseKeys(data, { deep: true })
                    : data,
            ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
        ],
        transformResponse: [
            ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
            (data) =>
                isPlainObjectOrArray(data)
                    ? camelcaseKeys(data, { deep: true })
                    : data,
        ],
    });
}

function isPlainObjectOrArray(
    data: unknown,
): data is Record<string, unknown> | Record<string, unknown>[] {
    return (
        !!data &&
        typeof data === 'object' &&
        (Array.isArray(data) || data.constructor === Object)
    );
}

function toUploadProgressHandler(
    onProgress: (percent: number) => void,
): (progressEvent: AxiosProgressEvent) => void {
    return (progressEvent) => {
        if (progressEvent.total) {
            onProgress(
                Math.round((progressEvent.loaded * 100) / progressEvent.total),
            );
        }
    };
}
